import { fontGroups } from '@zoopla-eng/zds-components/themes';
import { ButtonLinkRight } from '@zoopla-eng/zds-components/button';
import { ArrowRightSmall, SaveSmall } from '@zoopla-eng/zds-components/icons';
import { useBreakpointListener } from '@zoopla/dom-util';
import { ErrorBoundary } from '@zoopla/shared/components';
import {
    ListingCardPortraitSkeleton,
    ListingCardMiniSkeleton,
    TriggerWhenVisible
} from '@zoopla/listings/cards';
import { useEnrichedSavedPropertyIds } from './graphql/EnrichedSavedPropertyIds';
import { SavedListingCardDesktop } from './SavedListingsWidgetDesktop/SavedListingsWidgetDesktop';
import { SavedListingCardMobile } from './SavedListingsWidgetMobile/SavedListingsWidgetMobile';
import { MappedEnrichedSavedPropertyId } from './graphql/EnrichedSavedPropertyIds/useEnrichedSavedPropertyIds';
import { trackClickSeeAllSavedListings } from './SavedListingsWidget.tracking';
import {
    gridCardContentStyle,
    gridCardStyle,
    gridLinkStyle,
    gridTitleStyle,
    mobileLoadingContainerStyle,
    widgetGridStyle,
    instructionTextStyle,
    textStyle
} from './SaveListingsWidget.css';

export const LISTINGS_TO_DISPLAY_MOBILE = 3;
export const LISTINGS_TO_DISPLAY_DESKTOP = 4;

export const SavedListingsWidgetUniversal = ({
    listings,
    loading,
    isMobile
}: {
    listings: Array<MappedEnrichedSavedPropertyId>;
} & { loading: boolean; isMobile: boolean }) => {
    const Card = isMobile ? SavedListingCardMobile : SavedListingCardDesktop;
    const skeleton = isMobile ? (
        <div className={mobileLoadingContainerStyle}>
            <ListingCardMiniSkeleton testid="mobile-initial-skeleton" />
            <ListingCardMiniSkeleton testid="mobile-initial-skeleton" />
        </div>
    ) : (
        <>
            <ListingCardPortraitSkeleton testid="desktop-initial-skeleton" />
            <ListingCardPortraitSkeleton testid="desktop-initial-skeleton" />
        </>
    );

    return (
        <div className={widgetGridStyle}>
            <div className={gridLinkStyle}>
                <ButtonLinkRight
                    category="tertiary"
                    Icon={ArrowRightSmall}
                    data-testid="see-all-saved-properties"
                    onClick={trackClickSeeAllSavedListings}
                    href="/favourites/properties/"
                >
                    See all saved properties
                </ButtonLinkRight>
            </div>
            <div className={gridTitleStyle}>
                <h2 className={fontGroups.mug}>Recently saved properties</h2>
            </div>
            <div className={gridCardStyle}>
                <div className={gridCardContentStyle}>
                    {loading ? skeleton : null}
                    {listings.map(({ listingId }) => {
                        return (
                            <TriggerWhenVisible
                                triggerDelay={100}
                                key={listingId}
                            >
                                {(isTriggered: boolean) => {
                                    return (
                                        <Card
                                            listingId={listingId}
                                            isTriggered={isTriggered}
                                        />
                                    );
                                }}
                            </TriggerWhenVisible>
                        );
                    })}
                </div>
            </div>
            <div />
        </div>
    );
};

function SavedListingsContent() {
    const isMobile = useBreakpointListener(768, 'LT');

    const listingsCount = isMobile
        ? LISTINGS_TO_DISPLAY_MOBILE
        : LISTINGS_TO_DISPLAY_DESKTOP;

    const { savedListings, loading } = useEnrichedSavedPropertyIds();

    const listingsToDisplay = savedListings?.slice(0, listingsCount) || [];

    if (!loading && listingsToDisplay.length === 0) {
        return (
            <div>
                <h2
                    className={`${textStyle} ${fontGroups.mug}`}
                    data-testid="saved-properties-empty-message"
                >
                    You don&apos;t have any saved properties yet!
                </h2>
                <p className={`${instructionTextStyle} ${fontGroups.whisk}`}>
                    Click the <b>Save icon</b>&nbsp;
                    <SaveSmall /> on any property and we&apos;ll save it here
                    for you to view later
                </p>
            </div>
        );
    }

    return (
        <ErrorBoundary>
            <SavedListingsWidgetUniversal
                listings={listingsToDisplay}
                loading={loading}
                isMobile={isMobile}
            />
        </ErrorBoundary>
    );
}

export default SavedListingsContent;
